/**
 * №29 (S1, D-SS2) — WHAT THE WORK RAIL IS BOUND TO. Resolved once, read by all four
 * tabs, so Plan · Context · Receipts · Memory can never be about different things.
 *
 * D-SS2 fixes the default: *"the rail's bound context = the anchor"* — the same
 * matrix row the stage and the chat read, never a second derivation. On top of that
 * there is exactly one override, and it is the one the 22.3.1 follow/pin machine
 * already established:
 *
 *   **a focused RUN wins over the route's anchor.**
 *
 * Because that is what the human is actually watching. Clicking a decision card
 * anywhere focuses its run in the conversation (`focusTask` / `notifyAnswered`); the
 * rail follows it, so the plan beside the thread is that run's plan and the receipts
 * beside it are that run's receipts. Navigate away and FOLLOW re-anchors both.
 *
 * ## №29 (S2) — standing ON a run is focusing it
 *
 * The RUN SUBJECT (`/app/task/:taskId`, mockup D) made that override incomplete. Its
 * matrix row anchors on `{kind: "task", id}` — the run IS the route — but the run was
 * only ever "focused" by a click inside the conversation, so navigating straight to a
 * run left the rail bound to nothing: Plan said "this is a place, not one piece of
 * work" while the stage was rendering that exact piece of work. That was invisible
 * while `/app/task/:id` rendered its own second context rail (№22.1's), which S2
 * deletes — so the rule is stated instead of implied:
 *
 *   **a `task` anchor is a focused run.** Same override, one more way to arrive at it.
 *
 * A click still wins over the route (you can be on a run and look at another one),
 * which is why the focused task is read first and the route's task is the fallback.
 *
 * Everything below is READ ONLY and derived from records that already exist. Nothing
 * here invents a binding: when the anchor has no record, no run and no hire, the
 * fields are null and the tabs say so rather than drawing a plausible frame.
 */
import { useAppScope } from "../../../hooks/use-app-scope";
import { useCoPanel } from "../../collab/co-panel-context";
import { useTaskDetail } from "../../../hooks/use-task-detail";
import { useWorkspaceInfo } from "../../../../lib/hooks/use-kuvio";
import type { Task } from "../../../../lib/types";
// The SAME predicate the stage head, the anchor chip and the route journey read (S2, Q3 → B)
// — "is the conversation the subject here". A fourth reader, not a fourth definition.
import { conversationIsSubject } from "../../../routes/app/route-anchor-matrix";

export interface RailBinding {
  /** The anchor's name, as the shell renders it everywhere else (D-AF1). */
  label: string;
  /** The run being watched, when the conversation is on one. */
  taskId: string | null;
  task: Task | null;
  /** The entity row this work sits on — from the run's anchor, else the route's. */
  record: { type: string; id: string; name: string } | null;
  /** The member of staff carrying it, when one is named. D-L6: never "the agent". */
  hire: { id: string; name: string } | null;
  /** True while the focused run's detail is still arriving. */
  loading: boolean;
  /**
   * №29 (S4.1) — WHOSE PLAN the Plan tab may show, or `null` for nobody's.
   *
   * David, testing a matter on dev: the conversation showed the MATTER's workflow plan.
   * That was not an empty tab dressed as full — the plan was real. It was the wrong
   * OBJECT: two things share the word.
   *
   *   the rail's Plan   the workflow instance running on the record   — the matter's
   *   a chat's "plan"   what this turn is doing to answer you         — the conversation's
   *
   * So the tab answered a question about the record to someone who had asked a question in
   * a conversation. Off-topic content costs more than absent content, because you have to
   * read it before you can discard it.
   *
   * The rule this states: **the rail follows the SUBJECT's scope, not the anchor's.** The
   * machinery was already here — a focused run wins over the route's anchor — it simply was
   * not being consulted before falling back to the record:
   *
   *   "run"     a run is focused. Its steps ARE the conversation's plan when a conversation
   *             drove it, and the record's plan when you are standing on the run. Same
   *             object either way, which is why one value covers both.
   *   "record"  the RECORD is the subject. Its workflow plan is what you came to see —
   *             this is the case that must not regress.
   *   null      the conversation is the subject and no run is focused. There is no
   *             conversation-scoped plan, and the record's is a WRONG answer rather than a
   *             fallback. Nothing is the honest thing to show.
   *
   * Note what is NOT scoped this way: Receipts, Context and Memory stay anchor-scoped. They
   * are provenance and what-this-can-see — legitimately about the anchor, and legitimately
   * always relevant to a conversation about it. Plan is different because it presents steps
   * you might act on.
   */
  planScope: "run" | "record" | null;
}

export function useRailBinding(): RailBinding {
  const { anchor, chip, ctx, subject } = useAppScope();
  const panel = useCoPanel();
  const wsInfo = useWorkspaceInfo();

  // A run the user clicked wins; otherwise, if the ROUTE is a run, that is the run
  // they are watching. Both are "the focused run" — see the header.
  const routeTaskId = anchor.kind === "task" ? (anchor.id ?? null) : null;
  const focusedId = panel.focusedTask?.taskId ?? routeTaskId;
  const detail = useTaskDetail(focusedId ?? undefined, !!focusedId);
  const task = focusedId ? (detail.data?.task ?? null) : null;

  // The record: the focused run's anchor first (it is the more specific truth), then
  // the route's own anchor when it IS a record. A Place or the workspace has none.
  const runAnchor = task?.anchor ?? null;
  // The record's TYPE comes off the matrix row's own chat scope (`type_slug`), which
  // is where the route's `:entity` param is already declared. Re-reading the pathname
  // for it here would be a second resolver over the same URL — the F1 defect class.
  const record = runAnchor
    ? { type: runAnchor.type, id: runAnchor.no, name: runAnchor.name || runAnchor.no }
    : anchor.kind === "record" && anchor.id
      ? { type: ctx.scope.type_slug ?? "", id: anchor.id, name: chip }
      : null;

  // The hire: the run's own actor first, then the desk we are standing on.
  const roster = wsInfo.data?.library_root ?? [];
  const deskId = anchor.kind === "agent" ? anchor.id : undefined;
  const deskName = roster.find((n) => n.id === deskId)?.name;
  const hire = task?.agent_id
    ? { id: task.agent_id, name: task.agent_name ?? panel.focusedTask?.agentName ?? "" }
    : deskId
      ? { id: deskId, name: deskName ?? chip }
      : null;

  // Whose plan may be shown (see `planScope`). Ordered: a focused run wins, exactly as it
  // does for every other field; otherwise the conversation-as-subject case is the one that
  // must NOT fall through to the record.
  const planScope: RailBinding["planScope"] =
    focusedId ? "run"
      : conversationIsSubject(subject) ? null
        : record ? "record"
          : null;

  return {
    label: chip,
    taskId: focusedId,
    task,
    record,
    hire: hire && hire.name ? hire : hire ? { ...hire, name: chip } : null,
    loading: !!focusedId && detail.isLoading,
    planScope,
  };
}
